import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { v4 as uuid } from 'uuid';
import { Subject } from 'rxjs';
import { SwiperComponent, SwiperModule } from 'swiper/angular';
import { CommonModule } from '@angular/common';
import { SwiperOptions } from 'swiper/types';
import SwiperCore, { Navigation, Pagination, Scrollbar, Autoplay } from 'swiper';

SwiperCore.use([Navigation, Pagination, Scrollbar, Autoplay]);

@Component({
  selector: 'outlaw-carousel',
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SwiperModule, CommonModule],
})
export class CarouselComponent implements AfterViewInit, OnDestroy {
  @Input() hasIndex = false;

  @Output() private getItems = new EventEmitter();

  @Input() totalSlides: number;

  @Input() disabled = false;

  @Input() hasTabletStyles = false;

  @Input() isElementsCentered = false;

  @Input() set slides(slides: any[]) {
    this._slides = slides;
    if (this.hasNewSlides) {
      setTimeout(() => {
        if (this.swiper?.swiperRef) {
          this.swiper.swiperRef.slideNext();
        }
      }, 4);
      this.hasNewSlides = false;

      setTimeout(() => {
        if (this.swiper?.swiperRef?.isEnd && this.totalSlides > slides.length) {
          this.getItems.emit();
        }
      }, 10);
    }
  }

  @Input() set config(v: SwiperOptions) {
    if (v) {
      this.swiperConfig = {
        ...this.swiperConfig,
        ...v,
      };
    }
  }

  @Input() set isLoading(v: boolean) {
    if (this.isLoading && !v) {
      if (this.swiper?.swiperRef) {
        this.swiper.swiperRef.update();
      }
    }
    this._isLoading = v;
  }

  @ViewChild('swiper', { static: false }) private swiper: SwiperComponent;
  @ContentChild(TemplateRef) template: TemplateRef<any>;

  isFirstSlideLefterThanScreen = false;
  el = inject(ElementRef);

  private _slides: any[] = [];
  private _isLoading = false;
  private destroy$ = new Subject<void>();

  get slides(): any[] {
    return this._slides;
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  uniqueId: string = uuid();

  swiperConfig: SwiperOptions = {
    speed: 500,
    direction: 'horizontal',
    slidesPerView: 'auto',
    loop: false,
    navigation: {
      nextEl: '.swiper-button-next-' + this.uniqueId,
      prevEl: '.swiper-button-prev-' + this.uniqueId,
    },
    pagination: true,
    scrollbar: true,
    autoplay: false,
    spaceBetween: 32,
  };

  pageIndex = 0;
  private hasNewSlides = false;
  @Input() hasOnlyGradient = false;

  onReachEnd(): void {
    if (this.swiper?.swiperRef) {
      this.swiper.swiperRef.slideNext();
    }
    this.getItems.emit();
    this.hasNewSlides = true;
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (!this.swiper?.swiperRef) {
        return;
      }

      this.swiper.swiperRef.params.slidesPerView = 'auto';

      if (this.swiper.swiperRef.slides.length > 0) {
        const lastSlideIndex = this.swiper.swiperRef.slides.length - 1;
        const lastSlide = this.swiper.swiperRef.slides[lastSlideIndex];

        if (lastSlide) {
          this.swiper.swiperRef.on('slideChange', () => {
            if (this.swiper.swiperRef.isEnd) {
              this.swiper.swiperRef.update();
            }
          });
        }
      }

      this.swiper.swiperRef.update();

      if (this.swiper.swiperRef.isEnd && this.totalSlides > this.slides.length) {
        this.getItems.emit();
      }

      if (
        this.isElementsCentered &&
        this.swiper.swiperRef.slides.length &&
        this.swiper.swiperRef.slides[0].getBoundingClientRect().x < this.el?.nativeElement.getBoundingClientRect().x
      ) {
        this.isFirstSlideLefterThanScreen = true;
      }
    }, 0);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
